/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Mail, Lock, AlertCircle, CheckCircle2, User, Building2, Store } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // Default TRUE — mobile-first PWA, brugere skal ikke logge ud hver dag.
  // 30-dages JWT i stedet for 24-timers.
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [loginError, setLoginError] = useState('');
  // `loginErrorKind` drives a smart "Next step" CTA inside the error
  // block — no_account → "Opret konto", oauth_only → "Log ind med
  // Google", bad_password → "Glemt adgangskode?". The user no longer
  // has to guess what to do when login fails.
  const [loginErrorKind, setLoginErrorKind] = useState('');

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) { setEmail(savedEmail); setRememberMe(true); }
    const savedStay = localStorage.getItem('stayLoggedIn');
    if (savedStay === 'true') setStayLoggedIn(true);
  }, []);

  const validateEmail = (value) => {
    if (!value) return t('auth.emailRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return t('auth.invalidEmail');
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailErr = validateEmail(email);
    if (emailErr) { setEmailError(emailErr); setEmailTouched(true); return; }
    if (!password) { setLoginError(t('auth.passwordRequired')); return; }

    setLoading(true);
    setLoginError('');
    try {
      const user = await login(email, password, stayLoggedIn);
      if (rememberMe) localStorage.setItem('rememberedEmail', email);
      else localStorage.removeItem('rememberedEmail');
      if (stayLoggedIn) localStorage.setItem('stayLoggedIn', 'true');
      else localStorage.removeItem('stayLoggedIn');
      toast.success(`${t('common.welcome')}, ${user.name || user.nickname || 'bruger'}!`);
      navigate('/');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      const status = error.response?.status;
      // Status-aware error messaging — the backend now distinguishes
      // 404 (no account), 409 (Google-only account), 401 (bad password)
      // so the UI can offer the right next step instead of forcing the
      // user to guess between "Glemt adgangskode" and "Opret konto".
      setLoginError((detail && (t(detail) || detail)) || t('auth.loginFailedCheck'));
      setLoginErrorKind(status === 404 ? 'no_account' : status === 409 ? 'oauth_only' : 'bad_password');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = email && password && !emailError;

  return (
    <div className="min-h-screen flex flex-col">
      <Link 
        to="/" 
        className="absolute top-4 left-4 flex items-center gap-1.5 text-white/80 hover:text-white transition-colors z-10 py-2 px-3 rounded-lg hover:bg-white/10 text-sm"
        data-testid="back-to-home"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="font-medium" style={{textTransform:'none'}}>{t('common.back')}</span>
      </Link>

      <div className="flex-1 flex items-center justify-center p-4 py-16">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="flex items-center justify-center mb-6">
            <img loading="lazy" src="/assets/baremc-logo-transparent.png" alt="BareMC" className="h-16 w-auto drop-shadow-2xl" />
          </div>

          {/* Login Card */}
          <div className="glass-card p-6 overflow-hidden">
            <h1 className="text-2xl text-center text-white mb-1 tracking-wide">{t('auth.login')}</h1>
            <p className="text-center text-white/45 text-sm mb-1" style={{textTransform:'none'}}>{t('auth.welcomeBackTo')}</p>
            <div className="mc-accent-line mx-auto mb-6 mt-3" />

            {loginError && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2" data-testid="login-error-banner">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-red-400" style={{textTransform:'none'}}>{loginError}</p>
                  {loginErrorKind === 'no_account' && (
                    <Link to="/registrer" className="inline-block mt-2 text-xs font-semibold text-emerald-300 underline" data-testid="login-error-cta-register" style={{textTransform:'none'}}>
                      Opret en konto i stedet →
                    </Link>
                  )}
                  {loginErrorKind === 'oauth_only' && (
                    <p className="text-xs text-red-400/70 mt-1" style={{textTransform:'none'}}>
                      Brug Google-knappen længere nede på siden.
                    </p>
                  )}
                  {loginErrorKind !== 'no_account' && loginErrorKind !== 'oauth_only' && (
                    <Link to="/glemt-adgangskode" className="inline-block mt-2 text-xs font-semibold text-emerald-300 underline" data-testid="login-error-cta-forgot" style={{textTransform:'none'}}>
                      Glemt adgangskode? Nulstil her →
                    </Link>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-white/60 mb-1.5 flex items-center gap-1.5" style={{textTransform:'none'}}>
                  <Mail className="w-3.5 h-3.5 text-emerald-400/70" /> {t('auth.email')}
                </label>
                <div className="relative">
                  <input type="email" value={email}
                    onChange={(e) => { setEmail(e.target.value); setLoginError(''); if (emailTouched) setEmailError(validateEmail(e.target.value)); }}
                    onBlur={() => { setEmailTouched(true); setEmailError(validateEmail(email)); }}
                    placeholder="din@email.dk" required autoComplete="email"
                    className="input-mc w-full"
                    style={{ borderColor: emailTouched && emailError ? '#ef4444' : emailTouched && email && !emailError ? '#22c55e' : undefined }}
                    data-testid="email-input" />
                  {emailTouched && email && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {emailError ? <AlertCircle className="w-4 h-4 text-red-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                    </div>
                  )}
                </div>
                {emailTouched && emailError && <p className="text-xs text-red-500 mt-1 flex items-center gap-1" style={{textTransform:'none'}}><AlertCircle className="w-3 h-3" />{emailError}</p>}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-white/60 flex items-center gap-1.5" style={{textTransform:'none'}}>
                    <Lock className="w-3.5 h-3.5 text-emerald-400/70" /> {t('auth.password')}
                  </label>
                  <Link to="/glemt-adgangskode" className="text-xs font-medium text-emerald-400 hover:text-emerald-300" style={{textTransform:'none'}} data-testid="forgot-password-link">
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
                    placeholder="........" required autoComplete="current-password"
                    className="input-mc w-full pr-10" data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="remember-me" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded accent-emerald-500" data-testid="remember-me-checkbox" />
                  <label htmlFor="remember-me" className="text-xs text-white/50 cursor-pointer" style={{textTransform:'none'}}>{t('auth.rememberMe')}</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="stay-logged-in" checked={stayLoggedIn} onChange={(e) => setStayLoggedIn(e.target.checked)}
                    className="w-4 h-4 rounded accent-emerald-500" data-testid="stay-logged-in-checkbox" />
                  <label htmlFor="stay-logged-in" className="text-xs text-white/50 cursor-pointer" style={{textTransform:'none'}}>{t('common.stayLoggedIn')}</label>
                </div>
              </div>

              <button type="submit" disabled={loading || !isFormValid}
                className="w-full bg-emerald-400 text-zinc-900 font-bold py-3 rounded-xl hover:bg-emerald-300 transition-all shadow-lg shadow-emerald-400/20 disabled:opacity-50"
                style={{textTransform:'none'}}
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-zinc-900/30 border-t-zinc-900 rounded-full animate-spin" />
                    {t('auth.loggingIn')}
                  </span>
                ) : t('auth.login')}
              </button>
            </form>

            <p className="text-center text-white/45 text-sm mt-5" style={{textTransform:'none'}}>
              {t('auth.noAccount')} <Link to="/register" className="font-bold text-emerald-400 hover:text-emerald-300 hover:underline" data-testid="register-link">{t('auth.register')}</Link>
            </p>
          </div>

          {/* Quick register */}
          <div className="mt-6">
            <p className="text-center text-white/50 text-xs mb-3" style={{textTransform:'none'}}>{t('auth.orCreateNewAs')}</p>
            <div className="grid grid-cols-3 gap-2">
              <Link to="/register?type=user" className="glass-card p-3 text-center hover:border-emerald-400/30 transition-all" data-testid="register-user-btn">
                <User className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
                <span className="text-[10px] font-semibold text-white/60" style={{textTransform:'none'}}>{t('register.user')}</span>
              </Link>
              <Link to="/register?type=club" className="glass-card p-3 text-center hover:border-emerald-400/30 transition-all" data-testid="register-club-btn">
                <Building2 className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
                <span className="text-[10px] font-semibold text-white/60" style={{textTransform:'none'}}>{t('register.club')}</span>
              </Link>
              <Link to="/register?type=business" className="glass-card p-3 text-center hover:border-emerald-400/30 transition-all" data-testid="register-business-btn">
                <Store className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
                <span className="text-[10px] font-semibold text-white/60" style={{textTransform:'none'}}>Pitstop</span>
              </Link>
            </div>
          </div>
          <div className="text-center mt-4 text-[9px] text-white/30 font-mono" data-testid="app-version-stamp">
            BareMC v25.1 · build 2026-02-14
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
